import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Container,
    Grid,
    Skeleton,
    Stack,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import axios from "axios";
import { API_URL, APP_URL, buildFetchUrl } from "./config";
import { useParams, useSearchParams } from "react-router-dom";

const parseQrSettings = (value) => {
    if (!value) {
        return {};
    }

    if (typeof value === "object") {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        console.error("Unable to parse QR settings", error);
        return {};
    }
};

const ClientLanding = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [requestedWebsiteRecord, setRequestedWebsiteRecord] = useState("");

    const { id } = useParams();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                let businessId = id;
                let explicitWebsiteRecord = "";

                if (id && !/^\d+$/.test(id)) {
                    const recordResponse = await axios.get(API_URL + "get_restaurant.php", {
                        params: { id },
                        withCredentials: true,
                    });
                    const recordData = recordResponse.data || {};
                    if (Number(recordData.CategoryID) !== 3) {
                        throw new Error("The requested website record does not exist.");
                    }
                    businessId = String(recordData.business || "");
                    explicitWebsiteRecord = String(recordData.base36_id || id).trim();
                }

                const response = await axios.get(API_URL + "get_restaurants.php", {
                    params: { id: businessId },
                    withCredentials: true,
                });
                setRestaurants(Array.isArray(response.data) ? response.data : []);
                setRequestedWebsiteRecord(explicitWebsiteRecord);
            } catch (error) {
                console.error("Error fetching restaurants:", error);
                setErrorMessage(error?.response?.status === 404
                    ? "This record does not exist."
                    : error?.message || "Failed to load records. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchRestaurants();
    }, [id]);

    const orderedRestaurants = useMemo(() => {
        const priority = (record) => {
            const categoryId = Number(record.CategoryID);
            if (categoryId === 3) return 0;
            if (categoryId === 4) return 1;
            return 2;
        };

        return [...restaurants].sort((a, b) => {
            const priorityDiff = priority(a) - priority(b);
            if (priorityDiff !== 0) {
                return priorityDiff;
            }

            return String(a.name || "").localeCompare(String(b.name || ""));
        });
    }, [restaurants]);

    const featuredBusiness = useMemo(() => {
        const websiteRecords = orderedRestaurants.filter((restaurant) => Number(restaurant.CategoryID) === 3);
        if (websiteRecords.length === 0) {
            return null;
        }

        const requestedRecordBase36 = (requestedWebsiteRecord || searchParams.get("record") || "").trim();
        if (requestedRecordBase36) {
            const matchedRecord = websiteRecords.find((restaurant) => String(restaurant.base36_id || "") === requestedRecordBase36);
            if (matchedRecord) {
                return matchedRecord;
            }
        }

        return websiteRecords[0];
    }, [orderedRestaurants, requestedWebsiteRecord, searchParams]);

    const featuredBusinessSettings = useMemo(
        () => parseQrSettings(featuredBusiness?.qr_settings),
        [featuredBusiness]
    );

    const featuredHeader = featuredBusinessSettings.websiteHeader?.trim() || (featuredBusiness ? `Explore ${featuredBusiness.name}` : "Explore available records");
    const featuredSubheader = featuredBusinessSettings.websiteSubheader?.trim() || "Here are links to our services";

    const linkedRecords = useMemo(() => {
        const nonWebsiteRecords = orderedRestaurants.filter((restaurant) => Number(restaurant.CategoryID) !== 3);
        const visibleIds = Array.isArray(featuredBusinessSettings.websiteVisibleRecordIds)
            ? featuredBusinessSettings.websiteVisibleRecordIds.map((recordId) => Number(recordId)).filter((recordId) => Number.isInteger(recordId) && recordId > 0)
            : [];

        if (visibleIds.length === 0) {
            return nonWebsiteRecords;
        }

        return nonWebsiteRecords.filter((restaurant) => visibleIds.includes(Number(restaurant.id)));
    }, [orderedRestaurants, featuredBusinessSettings]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `radial-gradient(circle at top, ${alpha("#8b5cf6", 0.14)} 0%, transparent 36%),
                    linear-gradient(180deg, #f8fbff 0%, #eef7f4 100%)`,
                py: { xs: 4, md: 7 },
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    display: "flex",
                    flex: 1,
                }}
            >
                <Box
                    sx={{
                        maxWidth: 920,
                        mx: "auto",
                        width: "100%",
                        textAlign: "center",
                        px: { xs: 1, sm: 2 },
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Stack spacing={2} alignItems="center" sx={{ mb: { xs: 4, md: 6 } }}>
                            <Box
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 999,
                                    backgroundColor: alpha("#7c3aed", 0.1),
                                    border: `1px solid ${alpha("#7c3aed", 0.18)}`,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#6d28d9",
                                        letterSpacing: "0.12em",
                                        fontWeight: 800,
                                    }}
                                >
                                    Quick links
                                </Typography>
                            </Box>

                            <Typography
                                variant={isMobile ? "h4" : "h2"}
                                sx={{
                                    fontWeight: 800,
                                    lineHeight: 1.05,
                                    color: "#1f2937",
                                }}
                            >
                                {featuredHeader}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    maxWidth: 720,
                                    color: "#4b5563",
                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                }}
                            >
                                {featuredSubheader}
                            </Typography>
                        </Stack>

                        {loading ? (
                            <Grid container spacing={2} justifyContent="center">
                                {Array.from({ length: 10 }).map((_, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Skeleton
                                            variant="rounded"
                                            sx={{
                                                height: 74,
                                                borderRadius: "999px",
                                                transform: "none",
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : errorMessage ? (
                            <Box
                                sx={{
                                    mx: "auto",
                                    maxWidth: 620,
                                    px: { xs: 3, sm: 4 },
                                    py: 4,
                                    borderRadius: 4,
                                    backgroundColor: "white",
                                    border: `1px solid ${alpha("#ef4444", 0.18)}`,
                                    boxShadow: `0 20px 50px ${alpha("#ef4444", 0.08)}`,
                                }}
                            >
                                <Stack spacing={1.5} alignItems="center">
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#b91c1c" }}>
                                        Unable to load records
                                    </Typography>
                                    <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
                                        {errorMessage || "This record does not exist."}
                                    </Alert>
                                    <Button
                                        variant="contained"
                                        href={APP_URL}
                                        sx={{ textTransform: "none", fontWeight: 700 }}
                                    >
                                        Go to homepage
                                    </Button>
                                </Stack>
                            </Box>
                        ) : linkedRecords.length === 0 ? (
                            <Box
                                sx={{
                                    mx: "auto",
                                    maxWidth: 620,
                                    px: { xs: 3, sm: 4 },
                                    py: 5,
                                    borderRadius: 4,
                                    backgroundColor: alpha("#ffffff", 0.9),
                                    border: `1px solid ${alpha("#7c3aed", 0.12)}`,
                                    boxShadow: `0 20px 55px ${alpha("#7c3aed", 0.08)}`,
                                }}
                            >
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937", mb: 1.5 }}>
                                    No records available yet
                                </Typography>
                                <Typography sx={{ color: "#6b7280" }}>
                                    Once records are added, they will appear here as quick-access links.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    gap: { xs: 1.25, sm: 1.5, md: 2 },
                                }}
                            >
                                {linkedRecords.map((restaurant) => (
                                    <Button
                                        key={restaurant.base36_id ?? restaurant.id ?? restaurant.name}
                                        onClick={() => {
                                            window.location.href = buildFetchUrl(restaurant.base36_id);
                                        }}
                                        endIcon={<LaunchRoundedIcon />}
                                        sx={{
                                            px: { xs: 2.5, sm: 3 },
                                            py: { xs: 0.95, sm: 1.15 },
                                            minHeight: 54,
                                            borderRadius: 999,
                                            textTransform: "none",
                                            fontWeight: 800,
                                            fontSize: { xs: "1rem", sm: "1.08rem" },
                                            lineHeight: 1.2,
                                            color: "#4c1dff",
                                            background: "linear-gradient(135deg, #d9fbe6 0%, #c9f3e7 100%)",
                                            border: `1px solid ${alpha("#84cc16", 0.14)}`,
                                            boxShadow: `0 14px 30px ${alpha("#22c55e", 0.12)}`,
                                            maxWidth: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.333% - 16px)" },
                                            width: "fit-content",
                                            "& .MuiButton-endIcon": {
                                                ml: 1,
                                                color: alpha("#4c1dff", 0.7),
                                            },
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #ccf7df 0%, #b8efe0 100%)",
                                                transform: "translateY(-2px)",
                                                boxShadow: `0 20px 36px ${alpha("#22c55e", 0.18)}`,
                                            },
                                        }}
                                    >
                                        {restaurant.name}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Typography
                        component="p"
                        sx={{
                            mt: { xs: 4, md: 5 },
                            color: "#6b7280",
                            fontSize: "0.95rem",
                        }}
                    >
                        Powered by{" "}
                        <Box
                            component="a"
                            href={APP_URL}
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                                color: "#4c1dff",
                                fontWeight: 700,
                                textDecoration: "none",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            QR-Flex
                        </Box>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default ClientLanding;
